import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { RESET, useAtomCallback } from "jotai/utils";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { useContextPathNodeIds } from "../navigation/context.ts";
import { getContentRef, resolveSlotRefContentId, type SlotRefContentIds } from "../nodes/dereferenced/index.ts";
import { useGetNodeIdExists } from "../nodes/useGetNodeIdExists.ts";
import { getDesignerNodeByIdSelector, getParentContextByIdSelector, useGetContextNodeActions } from "../state/index.ts";
import { selectedNodeIdAtom, selectedResponseIdAtom } from "./selectionAtoms.ts";

export const useSetSelectedNodeId = () => {
  const setSelectedNodeId = useSetAtom(selectedNodeIdAtom);
  const setSelectedResponseId = useSetAtom(selectedResponseIdAtom);

  return useCallback(
    (id?: number) => {
      setSelectedNodeId(id || RESET);
      // Close any open response editor when the node changes (incl. page navigation), but keep
      // the selected aspect so the property-panel tab persists across node selection.
      setSelectedResponseId(RESET);
    },
    [setSelectedNodeId, setSelectedResponseId],
  );
};

export const useSelectedNodeId = () => {
  const selectedNodeId = useAtomValue(selectedNodeIdAtom);

  const onSelectedNodeIdChanged = useSetSelectedNodeId();

  return useMemo(
    () => [selectedNodeId, onSelectedNodeIdChanged] as [number | undefined, (id?: number) => void],
    [onSelectedNodeIdChanged, selectedNodeId],
  );
};

export const useSelectedNode = () => {
  const selectedNodeId = useAtomValue(selectedNodeIdAtom);
  const getNodeIdExists = useGetNodeIdExists();
  const getById = useSelector(getDesignerNodeByIdSelector);

  return useMemo(() => {
    return selectedNodeId && getNodeIdExists(selectedNodeId) ? getById(selectedNodeId) : undefined;
  }, [getById, getNodeIdExists, selectedNodeId]);
};

export const useIsNodeSelected = (nodeId: number) =>
  useAtomValue(useMemo(() => atom((get) => get(selectedNodeIdAtom) === nodeId), [nodeId]));

// For when you only want to get the selected node id on demand.
export const useGetIsNodeSelected = (): ((nodeId: number) => boolean) =>
  useAtomCallback(useMemo(() => (get, _set, nodeId: number) => get(selectedNodeIdAtom) === nodeId, []));

export const useSelectedNodeActions = () => {
  const navigate = useNavigate();
  const pathNodeIds = useContextPathNodeIds();
  const [selectedNodeId, setSelectedNodeId] = useAtom(selectedNodeIdAtom);
  const getNodeIdExists = useGetNodeIdExists();
  const getById = useSelector(getDesignerNodeByIdSelector);

  const selectedNode = useMemo(() => {
    return selectedNodeId && getNodeIdExists(selectedNodeId) ? getById(selectedNodeId) : undefined;
  }, [getById, getNodeIdExists, selectedNodeId]);

  const getNodeActions = useGetContextNodeActions();

  const nodeActions = useMemo(
    () =>
      selectedNode
        ? getNodeActions(selectedNode, {
            // TODO: This is a short term workaround; the ultimate solution
            // would capture any delete e.g. via subscribing to node state events
            // and be around route rather than selected node. This solves immediate problem for now
            onAction: (type, nodeId) => {
              if (type !== "delete") return;

              const parentNodeId = pathNodeIds.filter((id) => id !== nodeId).pop();
              setSelectedNodeId(parentNodeId);

              if (!parentNodeId) return;

              const { pathname } = window.location;
              void navigate(pathname.split(`/${parentNodeId}`)[0].concat(`/${parentNodeId}`));
            },
          })
        : [],
    [getNodeActions, navigate, setSelectedNodeId, pathNodeIds, selectedNode],
  );
  return nodeActions;
};

export const useIsSelectedAsContent = (nodeId: number) => {
  // inline useSelectedNode to improve performance:
  // we don't want to re-render every time the selection changes.
  const getById = useSelector(getDesignerNodeByIdSelector);
  const getParentContextById = useSelector(getParentContextByIdSelector);
  const getNodeIdExists = useGetNodeIdExists();
  return useAtomValue(
    useMemo(
      () =>
        atom((get) => {
          const selectedNodeId = get(selectedNodeIdAtom);
          if (selectedNodeId === undefined) {
            return false;
          }
          if (selectedNodeId === nodeId) {
            return true;
          }
          if (!getNodeIdExists(selectedNodeId)) {
            return false;
          }
          const selectedNode = getById(selectedNodeId);
          const contentRef = getContentRef(selectedNode);
          // A named (slotRef) slot resolves its content node via the host layoutable: the mapping
          // rides parentContext. Match against the id it resolves to so the resolved content is
          // selected with its slot.
          if (typeof contentRef === "object") {
            const parentContext = getParentContextById(selectedNodeId) as
              | { slotRefContentIds?: SlotRefContentIds }
              | undefined;
            return resolveSlotRefContentId(contentRef, parentContext?.slotRefContentIds) === nodeId;
          }
          return contentRef === nodeId;
        }),
      [nodeId, getById, getParentContextById, getNodeIdExists],
    ),
  );
};
